<template>
  <el-row :gutter="0" class="h-full">
    <el-col :span="20" class="relative">
      <div v-loading="!isReadyToScan || isLoadingScanMO" class="scan-view">
        <div>
          <div id="scan-area">
            <div id="video-area">
              <video playsinline autoplay class="opacity-0 " id="video"></video>
            </div>
          </div>
          <canvas id="captureCanvas" v-show="false"></canvas>
        </div>
        <div
          class="absolute z-50 flex justify-center items-center right-3 left-3 mb-2 flex-row"
          v-if="isMultipleScan && showMultipleScan"
          style="bottom: calc(27%)"
        >
          <ScannedValueInput v-model:valueCode="valueCode" @update:completeScan="handleCompleteScan" />
        </div>

        <div
          v-if="showMultipleScan"
          class="absolute z-50 right-8 flex top flex-col justify-center items-center"
          style="bottom: calc(20%)"
        >
          <BulkScanSwitchButton @update:isMultipleScan="isMultipleScan = $event" :isMultipleScan="isMultipleScan" />
        </div>

        <ScannerMessageOverlay :errorLines="overlayErrorLines" />
      </div>
    </el-col>
    <el-col :span="4" class="relative pt-10 side-panel">
      <DateTimeDisplay :currentTime="currentTime" />

      <div class="btn-complete">
        <el-button class="btn-complete-content" @click="showCompleteDialog"> {{ $t('complete') }}</el-button>
      </div>
    </el-col>
  </el-row>

  <CompleteConfirmationDialog v-model="isShowCompleteDialog" @confirm="handleConfirmComplete" />

  <ManufacturingPlanDialog
    v-model="isShowBarcodeDialog"
    :manufacturingPlan="manufacturingPlan"
    v-model:lotNumber="lotNumber"
    @begin="handleBegin"
    @close="handleCloseDialog"
  />
  <WorkCenterErrorDialog v-model="isOpenWorkCenterError" :errorMessage="workCenterErrorMessage" />

  <ConfirmDialog
    v-model="showManufacturingDateDialog"
    :message="$t('manufacturing_date_not_today')"
    :confirmText="$t('OK')"
    :cancelText="$t('cancel')"
    @confirm="onManufacturingDateDialogConfirm"
    @cancel="onManufacturingDateDialogCancel"
  />
</template>

<script lang="ts">
import BulkScanSwitchButton from '@/components/common/BulkScanSwitchButton.vue'
import DateTimeDisplay from '@/components/common/DateTimeDisplay.vue'
import ScannedValueInput from '@/components/common/ScannedValueInput.vue'
import ScannerMessageOverlay from '@/components/common/ScannerMessageOverlay.vue'
import CompleteConfirmationDialog from '@/components/dialog/CompleteConfirmationDialog.vue'
import ConfirmDialog from '@/components/dialog/ConfirmDialog.vue'
import ManufacturingPlanDialog from '@/components/dialog/ManufacturingPlanDialog.vue'
import WorkCenterErrorDialog from '@/components/dialog/WorkCenterErrorDialog.vue'
import BarcodeHandlerMixin, { BarcodeParseResult } from '@/components/mixins/BarcodeHandlerMixin.vue'
import BarcodeReaderMixin from '@/components/mixins/BarcodeReaderMixin.vue'
import ManufacturingDateCheckMixin from '@/components/mixins/ManufacturingDateCheckMixin.vue'
import { FETCH_BARCODE, SAVE_SCANNED_WORK_CENTER_CODE, UPDATE_SCANNED_RESULT } from '@/store/actions'
import { LOCAL_STORAGE_ITEM, UNIT_OPTIONS } from '@/utils/constants'
import { EBCRType } from '@/utils/enums'
import errorHandler, { parseErrorLines } from '@/utils/errorHandler'
import { logError } from '@/utils/errorLogger'
import { generateLotNumberFromDate, promptVideoPermission } from '@/utils/helpers'
import { scannerBridge } from '@/utils/scannerBridge'
import { TError } from '@/utils/types/commonTypes'
import { BrowserMultiFormatReader, Result } from '@zxing/library'
import { Barcode, BarcodeCapture } from 'scandit-web-datacapture-barcode'
import { mixins, Options } from 'vue-class-component'

@Options({
  components: {
    BulkScanSwitchButton,
    DateTimeDisplay,
    ScannerMessageOverlay,
    ScannedValueInput,
    CompleteConfirmationDialog,
    ConfirmDialog,
    ManufacturingPlanDialog,
    WorkCenterErrorDialog,
  },
  name: 'ScanView',
})
export default class ScanView extends mixins(BarcodeReaderMixin, ManufacturingDateCheckMixin, BarcodeHandlerMixin) {
  _barcodeDataCapture: BarcodeCapture | undefined
  codeReader = new BrowserMultiFormatReader()
  isReadyToScan = false
  isLoadingScanMO = false
  isHandlingAddScan = false
  video: HTMLVideoElement | null = null
  listBlob: Blob[] = []
  isShowBarcodeDialog = false
  isShowCompleteDialog = false
  overlayErrorLines: string[] = []
  currentTime: Date = new Date()
  lotNumber = ''
  private timeIntervalId: ReturnType<typeof setInterval> | null = null
  isOpenWorkCenterError = false
  workCenterErrorMessage = ''

  scanUnsubscribe: (() => void) | null = null
  statusUnsubscribe: (() => void) | null = null

  get unitOptions() {
    return UNIT_OPTIONS.map((unit) => ({ label: unit, value: unit }))
  }

  get defaultUnit(): string {
    return this.unitOptions[0]?.value || ''
  }

  get defaultAmount(): number {
    return 0
  }

  get defaultLimitAmount(): number {
    return 10
  }

  get barcodeInfo() {
    return this.$store.state.barcode?.barcode
  }

  get manufacturingPlan() {
    return this.$store.getters.manufacturingPlan
  }

  async defaultScanReader(result: Result, codeReader: BrowserMultiFormatReader) {
    this.isReadyToScan = true
    this.codeReader = codeReader
    if (!result) return

    const temp: { [key: string]: unknown } = { ...result }
    await this.handleScanResult((temp.text as string) || '')
  }

  async barcodeDetectorPolyFillScanReader(result: DetectedBarcode[]) {
    this.isReadyToScan = true
    if (result.length < 1) return
    await this.handleScanResult((result[0].rawValue as string) || '')
    if (!this.isMultipleScan) {
      this.vidOff()
    }
  }

  async scanditDataResult(symbology: Barcode) {
    this.isReadyToScan = true
    if (!symbology) return
    await this.handleScanResult(symbology.data || '')
  }

  get isScanningBlocked() {
    return this.isShowBarcodeDialog || this.isLoadingScanMO || this.isOpenWorkCenterError
  }

  get isAuth() {
    return this.$store.getters.isAuth
  }

  async mounted() {
    this.timeIntervalId = setInterval(() => {
      this.currentTime = new Date()
    }, 1000)
    this.currentTime = new Date()
    this.showMultipleScan =
      this.$store.state.project?.details?.cameraFeatures?.bulkCameraScanResultSetting?.isEnable ?? false

    if (this.machineId) {
      this.$store.dispatch(SAVE_SCANNED_WORK_CENTER_CODE, this.machineId)
    }

    if (localStorage.getItem('allowedCam')) {
      await this.initCameraSelection()
      await this.setupCameraSetting()
    } else {
      this.isReadyToScan = true
    }

    this.scanUnsubscribe = scannerBridge.onScan(async (data: string) => {
      console.log('Scanner Data:', data)
      const scannedValue = data.startsWith('true') ? data.substring(4) : data
      await this.handleScanResult(scannedValue.trim())
    })
  }

  unmounted() {
    if (this.timeIntervalId) {
      clearInterval(this.timeIntervalId)
      this.timeIntervalId = null
    }
    if (this.scanUnsubscribe) {
      this.scanUnsubscribe()
    }
    if (this.statusUnsubscribe) {
      this.statusUnsubscribe()
    }
  }

  async setupCameraSetting() {
    try {
      this.isUserAutheticated()
      this.isReadyToScan = false
      switch (this.barcodeReaderType as EBCRType) {
        case EBCRType.SCANDIT:
          await this.initScanditReader()
          break
        case EBCRType.BARCODE_DETECTION:
          await this.initBarcodeReaderPolyFill()
          break
        default:
          await promptVideoPermission()
          await this.initZxingReader()
          break
      }
    } catch (error) {
      logError('', {}, '', `setupCameraSetting error ${error}`)
      errorHandler(error as TError)
    } finally {
      this.isReadyToScan = true
    }
  }

  resetScannedValue() {
    this.listBlob = []
    this.valueCode = ''
    this.$store.dispatch(UPDATE_SCANNED_RESULT, this.listBlob)
  }

  redirectHome() {
    this.$router.replace({
      name: 'home',
    })
  }

  isUserAutheticated() {
    const isRestrictedPage = this.$route.meta.restricted
    if (!this.isAuth && isRestrictedPage) {
      this.redirectHome()
    }
  }

  async handleCompleteScan() {
    if (!this.isMultipleScan || this.valueCode.trim() === '') {
      return
    }
    this.processScannedText(this.valueCode)
  }

  async setupVideo() {
    if (!this.video) {
      this.video = document.getElementById('video') as HTMLVideoElement
    } else {
      this.video.srcObject = null
    }

    const videoConstraints = this.selectedCamera
      ? { aspectRatio: 1, deviceId: { exact: this.selectedCamera } }
      : { facingMode: 'environment', aspectRatio: 1 }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints })
      await this.setStream(stream)
    } catch (e) {
      const error = e as Error & { name?: string }
      logError('', {}, '', `setupVideo error ${error}`)
      errorHandler(error.name === 'NotAllowedError' ? 'camera_access_not_allow' : error)
    }
  }

  async snapshot() {
    try {
      const tempCanvas = document.querySelector('#captureCanvas') as HTMLCanvasElement
      const video = document.querySelector('video')
      if (!video || !tempCanvas) return
      tempCanvas.width = video.videoWidth
      tempCanvas.height = video.videoHeight
      const context = tempCanvas.getContext('2d')
      if (!context) return
      context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight)
      const blob = await new Promise((resolve) => tempCanvas.toBlob(resolve))
      return blob
    } catch (error) {
      logError('', {}, '', `snapshot error ${error}`)
      console.error('Error during snapshot:', error)
    }
  }

  handleSaveScannedInfo(blob: Blob) {
    this.listBlob.push(blob)
    this.$store.dispatch(UPDATE_SCANNED_RESULT, this.listBlob)
  }

  async handleScanResult(plainText: string) {
    if (this.isScanningBlocked) return
    this.isMultipleScan ? await this.handleMultipleScan(plainText) : await this.handleSingleScan(plainText)
  }

  async handleMultipleScan(plainText: string) {
    if (this.isHandleMultipleScan) {
      return
    }
    const blob = await this.snapshot()
    this.isHandleMultipleScan = true
    this.$confirm(this.$t('confirm_scanned_value', { item: plainText }), this.$t('info'), {
      confirmButtonText: this.$t('confirm'),
      confirmButtonClass: 'danger',
      cancelButtonText: this.$t('cancel'),
      closeOnClickModal: true,
      showCancelButton: true,
    })
      .then(() => {
        this.valueCode = this.processValueCode(plainText)
        this.handleSaveScannedInfo(blob as Blob)
      })
      .finally(() => {
        this.isHandleMultipleScan = false
      })
  }

  async handleSingleScan(plainText: string) {
    // const blob = await this.snapshot()
    // this.handleSaveScannedInfo(blob as Blob)
    this.valueCode = plainText
    if (plainText.toLowerCase().startsWith('false')) {
      this.overlayErrorLines = [this.$t('reading_error1'), this.$t('reading_error2'), this.$t('reading_error3')]
      return
    }
    this.processScannedText(plainText)
  }

  async handleBarcodeResult(result: BarcodeParseResult) {
    if (result.isNewFormat || result.isOldFormat) {
      this.isLoadingScanMO = true
      try {
        await this.$store.dispatch(FETCH_BARCODE, { id: result.barcodeId })
        if (this.checkManufacturingDate()) {
          return
        }
        this.lotNumber = generateLotNumberFromDate(this.manufacturingPlan?.manufactureCompletionDate || '')
        this.overlayErrorLines = []
        this.isShowBarcodeDialog = true
      } catch (error) {
        logError('', {}, '', `handleBarcodeResult error ${error}`)
        this.overlayErrorLines = parseErrorLines(error as TError)
      } finally {
        this.isLoadingScanMO = false
      }
    } else {
      this.overlayErrorLines = [this.$t('reading_error1'), this.$t('reading_error2'), this.$t('reading_error3')]
    }
  }

  get currentManufacturingWRKC() {
    return this.barcodeInfo?.manufacturingPlan?.workCenterCode || ''
  }

  scannedWorkCenterCode() {
    return localStorage.getItem(LOCAL_STORAGE_ITEM.WORK_CENTER_CODE)
  }

  get machineId(): string {
    return (this.$route.params.machine as string) || ''
  }

  handleBegin() {
    const barcodeId = this.barcodeInfo?.id
    if (!this.scannedWorkCenterCode()?.includes(this.currentManufacturingWRKC)) {
      this.workCenterErrorMessage = 'work_center_code_mismatch'
      logError('', {}, '', this.$t('work_center_code_mismatch'))
      this.isOpenWorkCenterError = true
      return
    }

    if (barcodeId && this.machineId) {
      this.$router.push({
        name: 'scan-tablet-operator-with-barcode',
        params: {
          machine: this.machineId,
          barcodeId: barcodeId.toString(),
        },
      })
    }
  }

  showCompleteDialog() {
    this.isShowCompleteDialog = true
  }

  handleConfirmComplete() {
    scannerBridge.closeApp()

    this.isShowCompleteDialog = false
    // this.redirectHome()
  }

  async handleCloseDialog() {
    this.isShowBarcodeDialog = false
    this.lotNumber = ''
  }
}
</script>

<style lang="scss">
@import './src/assets/css/mixins.scss';

.scan-view {
  position: relative;
  height: 100vh;
  background: rgb(32, 32, 32);
}

.scan-view video {
  height: 100vh;
}

#video-area {
  position: relative;

  #video {
    pointer-events: none !important;
  }
}

.btn-complete {
  bottom: 10px;
  position: absolute;
  width: 100%;
  display: flex;
  justify-content: center;
}

.btn-complete-content {
  background-color: #ceff00;
  font-size: 40px;
  font-weight: bold;
  color: red;
  width: 90%;
  border-radius: 0px !important;
  border-top: 2px solid white;
  border-left: 2px solid white;
  border-right: 2px solid black;
  border-bottom: 2px solid black;
}

.side-panel {
  padding-top: 14px;
  padding-bottom: 1rem;
  background: #bdd608;
}
</style>
